import React, { memo, useCallback, useMemo, useState, type ComponentType } from 'react';

import { usePageContentId } from '@confluence/page-context';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';

// eslint-disable-next-line import/no-cycle
import { CommentBatch } from './CommentBatch.tsx';

export const BATCH_SIZE = 8;

// Binary search to find the transition point between inline/general comments
const findTransitionIndex = (commentsList: { type: CommentType }[]) => {
	if (!commentsList.length) return 0;

	let low = 0;
	let high = commentsList.length - 1;

	// If all comments are of the same type, return 0
	const firstType = commentsList[0].type;
	const lastType = commentsList[high].type;
	if (firstType === lastType) return 0;

	while (low < high) {
		const mid = Math.floor((low + high) / 2);
		if (commentsList[mid].type === CommentType.INLINE) {
			low = mid + 1;
		} else {
			high = mid;
		}
	}
	return low;
};

export type BatchCallbackProps = {
	onCommentBatchRendered?: () => void;
	onLastThreadRendered?: () => void;
};

/** Props that are threaded through the batch renderer to each rendered comment component. */
export type PassThroughProps = BatchCallbackProps & Record<string, unknown>;

export type SharedCommentBatchProps<TComment> = {
	passThroughProps: PassThroughProps;
	Component: ComponentType<any>;
	getElementsToBatch: (
		batchedList: TComment[],
		passThroughProps: PassThroughProps,
		Component: ComponentType<any>,
		transitionIndexWithinBatch: number | undefined,
	) => React.JSX.Element[];
};

type CommentBatchListProps<TComment> = SharedCommentBatchProps<TComment> & {
	commentsList: TComment[];
};

const CommentBatchListInner = <TComment extends { type: CommentType }>({
	commentsList,
	passThroughProps,
	Component,
	getElementsToBatch,
}: CommentBatchListProps<TComment>) => {
	const [contentId] = usePageContentId();

	const batchCount = Math.ceil(commentsList.length / BATCH_SIZE);
	const batches = useMemo(() => new Array(batchCount).fill(0), [batchCount]);
	const [batch, setBatch] = useState(0);

	const onBatchRendered = useCallback((batchIndex) => {
		if (passThroughProps) {
			passThroughProps.onCommentBatchRendered && passThroughProps.onCommentBatchRendered();

			if (batches.length && batchIndex === batches.length - 1) {
				passThroughProps.onLastThreadRendered && passThroughProps.onLastThreadRendered();
			}
		}

		setBatch(batchIndex + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const globalTransitionIndex = useMemo(() => findTransitionIndex(commentsList), [commentsList]);
	const transitionBatchIndex = Math.floor(globalTransitionIndex / BATCH_SIZE);
	const transitionIndexWithinBatch = globalTransitionIndex % BATCH_SIZE;

	return (
		<>
			{batches.map((_, index) => {
				const isTransitionBatch = index === transitionBatchIndex && globalTransitionIndex !== 0;
				return (
					<CommentBatch
						key={`${contentId}-batch-${index}`}
						commentsList={commentsList}
						canRenderBatch={index <= batch}
						batchIndex={index}
						passThroughProps={passThroughProps}
						Component={Component}
						getElementsToBatch={getElementsToBatch}
						onBatchRendered={onBatchRendered}
						transitionIndexWithinBatch={isTransitionBatch ? transitionIndexWithinBatch : undefined}
					/>
				);
			})}
		</>
	);
};

// `memo` does not preserve generics, so we cast the memoized component back to a generic
// function type to keep `TComment` inferrable at the call site.
export const CommentBatchList = memo(CommentBatchListInner) as typeof CommentBatchListInner;
